// helper/utils.js
import Graphic from "@arcgis/core/Graphic";
import Extent from '@arcgis/core/geometry/Extent'; // Optional: Use if you want clean expansion padding
import { when } from "@arcgis/core/core/reactiveUtils.js";

import { defaultPinSymbol } from "@libs/utils/mapUtils.js";
import { LOG_MODULES, APP_CONFIG } from "@config/config";
import { log } from "@libs/utils/helper.js";
import { niogemsGisWellApi } from '@/libs/utils/apiEndpointList';

const QueryId = APP_CONFIG.MAPS.Well.queryField;
const isNeedLog = false;
/**
 * Shared Helper: Formats IDs based on the Field Type and QueryId.
 * Ensures WELL_ID gets braces {} and all strings are escaped.
 */
const formatIdsForQuery = (ids, layer) => {
  const idArray = Array.isArray(ids) ? ids : [ids];
  const apiField = layer.fields?.find(f => f.name === QueryId);
  const needsQuotes = apiField?.type === "string";

  const formattedValues = idArray.map(id => {
    // 1. Specific logic for WELL_ID (UUIDs with braces)
    if (QueryId === APP_CONFIG.MAPS.Well.specialWellId) {
      const wrappedId = `{${String(id).replace(/[{}]/g, "")}}`;
      return `'${wrappedId.replace(/'/g, "''")}'`;
    }

    // 2. Standard String logic
    if (needsQuotes) {
      return `'${String(id).replace(/'/g, "''")}'`;
    }

    // 3. Numeric logic (fallback to string if NaN to prevent query syntax errors)
    return isNaN(id) ? `'${id}'` : `${Number(id)}`;
  });

  return formattedValues;
};

/**
 * Zoom to one or many wells by API number(s).
 */
export async function zoomToWells(mapView, layer, ids, hasSel = false, highlightLayer = null,zoom=APP_CONFIG.MAPS.Well.detailZoom) {
  if (!mapView || !layer || !ids) return;

  const idArray = Array.isArray(ids) ? ids : [ids];
  if (idArray.length === 0) return;

  try {
    const query = layer.createQuery();
    const formattedIds = formatIdsForQuery(ids, layer);
   
    if (hasSel||formattedIds.length==1) {
       if(isNeedLog) log(LOG_MODULES.WELLARCGISMAPPER, "zoomToWells Single Selection (Pin + Zoom):", ids);
      // Mode A: Single Selection (Pin + Zoom)
      query.where = `${QueryId} = ${formattedIds[0]}`;
      query.returnGeometry = true;
      query.outFields = [QueryId];

      const featureRes = await layer.queryFeatures(query);
      if (featureRes?.features?.length > 0) {
        const feature = featureRes.features[0];

        if (highlightLayer) {
          highlightLayer.removeAll();
          highlightLayer.add(new Graphic({
            geometry: feature.geometry,
            symbol: defaultPinSymbol
          }));
        }
        await mapView.goTo({ target: feature.geometry, zoom}, { duration: 1000 });
      }
    } else { 
      if(isNeedLog) log(LOG_MODULES.WELLARCGISMAPPER, "zoomToWells (Extent Zoom):", formattedIds);
      // Mode B: Checked Items (Extent Zoom)
      query.where = `${QueryId} IN (${formattedIds.join(",")})`;

      const extentRes = await layer.queryExtent(query);
      //console.log("Extent Result:", extentRes); // <-- ADD THIS LINE

      if (extentRes?.count > 0 && extentRes.extent) {
        if (highlightLayer) highlightLayer.removeAll();
        const navTarget = extentRes.extent.expand(1.5);

        //console.log("Navigating to extent:", navTarget.toJSON()); // <-- ADD THIS LINE
        await mapView.goTo(navTarget, { duration: 1000, easing: "ease-in-out" });
      }
    }
  } catch (err) {
    console.error("[zoomToWells] Error:", err);
  }
}


export async function zoomToReservation(mapView, reservationWhere) { //console.error("00000 reservationWhere ",APP_CONFIG.MAPS.Reservation.endPointUrl, reservationWhere);
  if (!mapView || !reservationWhere) return;

  try {
    if(isNeedLog) log(LOG_MODULES.WELLARCGISMAPPER, `Fetching bounding box via API wrapper for: ${reservationWhere}`);
    // Firing the GET request through your established, authenticated api client
    
    const response = await niogemsGisWellApi.get(APP_CONFIG.MAPS.Reservation.endPointUrl, {
      params: {
        where: reservationWhere,
        returnExtentOnly: true, // Tells the server to only process and return the bounding box
        outSR:mapView.spatialReference.wkid ?? 102100,
        f: 'json'
      }
    });
    //if(isNeedLog) console.error("zoomToReservation response",response);
    const extentData = response?.data?.extent;
    if(isNeedLog){
      //console.error("1111111111 reservationWhere ",APP_CONFIG.MAPS.Reservation.endPointUrl, reservationWhere);
      console.error("zoomToReservation extentData",extentData);
    }

    if (extentData) {
      // Option A: Convert it to a real ArcGIS Extent object to use the .expand() padding trick
      const spatialExtent = Extent.fromJSON(extentData);
      await mapView.goTo({
        target: spatialExtent.expand(1) // Adds 20% margin around the reservation outline
      });

       
      // Option B: If you prefer zero SDK imports inside this function, 
      // you can pass the raw JSON data object directly to target:
      // await mapView.goTo({
      //   target: extentData 
      // });
     
      
    } else {
      throw new Error("No extent geometry properties returned in JSON envelope.");
    }

  } catch (err) {
    console.error('[zoomToReservation] api method wrapper execution failed:', err);
    
    // Safety baseline window zoom fallback
    mapView.goTo({
      center: APP_CONFIG.MAPS.Well.center,
      zoom:   APP_CONFIG.MAPS.Well.appZoom,
    });
  }
}

/**
 * Toggles an ID in the selection list.
 */
export function toggleSelection(id, handlersRef) {
  if (!id) return false;

  const currentChecked = handlersRef.current.checkedRds ?? [];
  const isCurrentlySelected = currentChecked.includes(id);

  const updated = isCurrentlySelected
    ? currentChecked.filter(item => item !== id)
    : [...currentChecked, id];

  handlersRef.current.onSingleClick(updated);

  return isCurrentlySelected;
}


// In helper/utils.js


export async function syncTableHighlights(featureTable, checkedRds) {
  if (!featureTable) return;

  // *** THIS IS THE CRITICAL FIX ***
  // Wait until the feature table's internal state is 'ready'.
  // This guarantees that the table has finished loading its data.
 // 1. Safe, non-reactive initialization check. 
  // Wait for the Web Component itself to be upgraded and registered in the DOM.
  //await featureTable.componentOnReady();

  const targetLayer = featureTable.layer;
  const oidField = targetLayer.object_idField || "OBJECTID";

  if (!checkedRds || checkedRds.length === 0) {
    featureTable.highlightIds?.removeAll();
    return;
  }

  // Now that the table is ready, it's safe to query and highlight.
  const formattedIds = formatIdsForQuery(checkedRds, targetLayer);
  const query = targetLayer.createQuery();
  query.where = `${QueryId} IN (${formattedIds.join(",")})`;
  query.outFields = [oidField];

  try {
    const results = await targetLayer.queryFeatures(query);
    const oids = results.features.map(f => f.attributes[oidField]);

    // It's now safe to call addMany because the table is ready.
    featureTable.highlightIds.removeAll();
    if (oids.length > 0) {
        featureTable.highlightIds.addMany(oids);
    }

    // You can also safely scroll now.
    if (oids.length > 0) {
        try {
            featureTable.scrollToRow(oids[0]);
        } catch (e) {
            // It's still good practice to catch scroll errors.
            console.warn("Scroll to row failed, but this is non-critical.");
        }
    }
  } catch (err) {
    console.error("[syncTableHighlights] Sync error:", err);
  }
}

